
import axios, { AxiosError, AxiosRequestConfig, Method } from 'axios'

import { getCopTokenAuthConfig } from '../components/SilentTokenProvider/config'
import { isCopEnabled } from './cookieUtils'
import { buildCopHeaders, waitForCopToken } from './tokenUtils'

const REQUEST_TIMEOUT_MS = 30_000
const MAX_RESPONSE_BYTES = 10 * 1024 * 1024

type SendRequestOptions = {
  data?: unknown
  responseType?: AxiosRequestConfig['responseType']
  headers?: Record<string, string>
}

// CopAppConfiguration OSGi config (allowedOrigins property) — see CopAppConfigService.
const getAllowedOrigins = (): ReadonlySet<string> => new Set(getCopTokenAuthConfig().allowedOrigins)

const buildSafeUrl = (url: string): string => {
  if (typeof url !== 'string' || url.trim() === '') {
    throw new Error('Invalid request URL')
  }

  const resolved = new URL(url, window.location.origin)

  if (resolved.protocol !== 'https:' && resolved.protocol !== 'http:') {
    throw new Error('Unsupported URL protocol')
  }

  const isSameOrigin = resolved.origin === window.location.origin
  const isAllowedOrigin = getAllowedOrigins().has(resolved.origin)

  if (!isSameOrigin && !isAllowedOrigin) {
    throw new Error(`Origin not allowed: ${resolved.origin}`)
  }

  return resolved.toString()
}

const sendRequest = async <T>(method: Method, url: string, options?: SendRequestOptions): Promise<T> => {
  // Centralized COP gate: no COP API request leaves the browser unless the user
  // is a COP/PowerPortal member (cop_powerportal_flag cookie === 'true'). This is
  // a UX/optimization guard only — real authorization is enforced server-side.
  if (!isCopEnabled()) {
    throw new Error('COP is not enabled for this user')
  }

  const safeUrl = buildSafeUrl(url)
  // Wait for the silent token on a cold first load so on-mount COP fetches don't
  // race token acquisition and 401. Falls back after a timeout so it never hangs.
  const headers = options?.headers ?? buildCopHeaders(await waitForCopToken())
  const config: AxiosRequestConfig = {
    method,
    url: safeUrl,
    headers,
    timeout: REQUEST_TIMEOUT_MS,
    responseType: options?.responseType ?? 'json',
    withCredentials: false,
    maxContentLength: MAX_RESPONSE_BYTES,
    maxBodyLength: MAX_RESPONSE_BYTES,
    validateStatus: status => status >= 200 && status < 300,
  }

  if (options?.data !== undefined) {
    const raw = options.data
    const contentType = String((headers as Record<string, string>)['Content-Type'] ?? '')
    const isPlainObject =
      raw !== null &&
      typeof raw === 'object' &&
      !(raw instanceof FormData) &&
      !(raw instanceof URLSearchParams) &&
      !ArrayBuffer.isView(raw) &&
      !(raw instanceof ArrayBuffer)
    config.data = isPlainObject && contentType.includes('application/json')
      ? JSON.stringify(raw)
      : raw
  }

  try {
    const response = await axios.request<T>(config)
    return response.data
  } catch (error) {
    const axiosError = error as AxiosError
    const statusMessage = axiosError.response?.status ? ` with status ${axiosError.response.status}` : ''
    throw new Error(`API request failed${statusMessage}`)
  }
}

export const get = <T = unknown>(url: string): Promise<T> => sendRequest<T>('GET', url)

export const getBlob = (url: string): Promise<Blob> => sendRequest<Blob>('GET', url, { responseType: 'blob' })

export const post = <T = unknown>(url: string, body?: unknown): Promise<T> => sendRequest<T>('POST', url, { data: body })

export const put = <T = unknown>(url: string, body?: unknown): Promise<T> => sendRequest<T>('PUT', url, { data: body })

export const del = <T = unknown>(url: string): Promise<T> => sendRequest<T>('DELETE', url)

export const postFormData = <T = unknown>(url: string, formData: FormData): Promise<T> => {
  return waitForCopToken().then(token => {
    const headers = buildCopHeaders(token)
    delete headers['Content-Type']
    return sendRequest<T>('POST', url, { data: formData, headers })
  })
}